import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormControl } from '@material-ui/core';
import cx from 'classnames';
import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import { ReactEditor } from 'slate-react';

import {
  focusEditor,
  insertFileTextToPrompt,
  PROMPT_MENU_TYPE,
  RichSlateBrandBriefsContext,
  useRichSlateEditor,
} from 'src/components/RichSlate';
import { MAX_RESOURCE_PER_TYPE } from 'src/data/common';
import { GENERATION_TOOL } from 'src/data/generationTool';
import {
  getResourceDocumentErrorMessage,
  getResourceImageErrorMessage,
  RESOURCE_CONTENT_TYPE,
  RESOURCE_CONTENT_TYPE_DIRECT_UPLOAD,
  RESOURCE_FILE_UPLOAD_ERROR_CODE,
} from 'src/data/performance';

import { Footer } from './components/Footer';
import { InstructionsTextareaWrapper } from './components/InstructionsTextareaWrapper';
import { PromptMenu } from './components/PromptMenu';
import { useHandlers } from './useHandlers';
import { useMenuSupport } from './useMenuSupport';
import useStyles from './useStyles';

const DEFAULT_MENU_PROPS = {
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  autoFocus: true,
};

export const PromptInstructions = ({
  className,
  error,
  value,
  lastPromptAsset,
  lastPromptUrl,
  onChange,
  disabled,
  onSuggestPromptClick,
  isSuggestPromptLoading,
  onSave,
  brandBriefs,
  brandBriefsMenuItems,
  onBankMessageClick,
  hideActions,
  editableClasses,
  loadingFreestyleToolContent,
  freestyleToolContentId,
  generationTool,
  urlResources,
  imageResources,
  addPromptImage,
  isSavePopup,
  onEditorFocus,
  freestyleToolId,
  shortExpanding,
  noTags,
  autoShowMenu,
  showOnFocus,
  disableAutoFocus,
  placeHolder,
  isFirstField,
  plusButtonRef,
  setPlusButtonRef,
  noMinMaxHeight,
  promptImages,
  openAddResourcePopup,
  isUploadingImage,
  setIsUploadingImage,
  setImageError,
  clearImageError,
  isApplicationModeInfinity,
  trackingEvents,
  hideImages,
  setImageUploadSuccess,
  isHtmlEmpty,
  addPromptDocument,
  isUploadingDocument,
  setIsUploadingDocument,
  setDocumentError,
  promptDocuments,
  documentResources,
  setDocumentUploadSuccess,
  clearDocumentError,
  setUploadingDocumentName,
  uploadingDocumentName,
  openConfirmPopup,
  index,
  setShouldUpdateFileText,
  shouldUpdateFileText,
  shouldOpenConfirmPopup,
  disablePaste,
  isPersonalizedPrompt,
  ratingsCount,
  showRatingsBanner,
  setShowRatingsBanner,
  onClearVariationRatings,
  showPersonalizePrompt,
  cachedValue,
  removePromptDocument,
  removePromptImage,
  isMultiPrompt,
  isSlim,
  uploadingDocuments,
  removeUploadingDocument,
  removeUploadingImage,
  uploadingImages,
  onDisplayMessage,
  editorDisabled,
  externalChannelPromptMenuProps,
  onAction,
}) => {
  const styles = useStyles();
  const [menuType, setMenuType] = useState(PROMPT_MENU_TYPE.default);
  const [plusIconClicked, setPlusIconClicked] = useState(false);
  const [isFirstRefVisible, setIsFirstRefVisible] = useState(false);
  const [showAllResources, setShowAllResources] = useState(false);

  const firstFieldRef = useRef();
  const menuRef = useRef(null);
  const pasteRef = useRef(null);

  const {
    menuAnchor,
    menuVisible,
    handleCloseMenu,
    handleMenuButtonClick,
    menuProps,
    handleMenuPropsChange,
    setWasOpened,
    searchValue,
    setSearchValue,
    quickSearchMode,
    handleQuickSearchTriggered,
  } = useMenuSupport(DEFAULT_MENU_PROPS, setPlusIconClicked);

  const editor = useRichSlateEditor();

  useEffect(() => {
    // when tool id changes, focus at end
    if (!ReactEditor.isFocused(editor) && !disableAutoFocus) {
      focusEditor({ editor, atEnd: true });
    }
  }, [freestyleToolId, editor, disableAutoFocus]);

  useEffect(() => {
    // when tool id changes, focus at end
    if (isFirstRefVisible) {
      focusEditor({ editor, atEnd: true });
      setIsFirstRefVisible(false);
    }
  }, [editor, isFirstRefVisible]);

  const {
    handleUrlClick,
    handleBankMessageClick,
    handleKeyDown,
    handleOpenMenuAtCursor,
    handleKeyDownMenu,
    handleImageClick,
    handleDocumentClick,
    handleOpenResourceInNewTab,
  } = useHandlers({
    editor,
    handleMenuButtonClick,
    handleMenuPropsChange,
    onBankMessageClick,
    handleCloseMenu,
    menuRef,
    addPromptImage,
    addPromptDocument,
    setIsUploadingImage,
    setIsUploadingDocument,
    isHtmlEmpty,
    promptImages,
    promptDocuments,
    setUploadingDocumentName,
    index,
    shouldOpenConfirmPopup,
    onDisplayMessage,
  });

  useEffect(() => {
    if (shouldUpdateFileText) {
      insertFileTextToPrompt(
        editor,
        isHtmlEmpty,
        shouldUpdateFileText.file,
        shouldUpdateFileText.type,
        false
      );
      setShouldUpdateFileText({ ...shouldUpdateFileText, index: null });
    }
  }, [shouldUpdateFileText, setShouldUpdateFileText, editor, isHtmlEmpty]);

  const uploadFile = useCallback(
    async (file) => {
      if (!file) {
        return;
      }

      const type = file.type.startsWith(RESOURCE_CONTENT_TYPE.image)
        ? RESOURCE_CONTENT_TYPE.image
        : RESOURCE_CONTENT_TYPE.document;

      const randomId =
        (type === PROMPT_MENU_TYPE.image ? 'uploadImage-' : 'uploadDocument-') + uniqueId();

      switch (type) {
        case RESOURCE_CONTENT_TYPE.image:
          setIsUploadingImage(true, randomId, index);
          break;
        case RESOURCE_CONTENT_TYPE.document:
          setUploadingDocumentName(file.name);
          setIsUploadingDocument(true, file.name, randomId, index);
          break;
        default:
          break;
      }

      const uploadedFile = await openAddResourcePopup({
        type:
          type === RESOURCE_CONTENT_TYPE.image
            ? RESOURCE_CONTENT_TYPE_DIRECT_UPLOAD.img
            : RESOURCE_CONTENT_TYPE_DIRECT_UPLOAD.document,
        onHiddenError: (error, id) => {
          if (id) {
            const resources =
              type === RESOURCE_CONTENT_TYPE.image ? imageResources : documentResources;
            const file = resources.find((resource) => resource.id === id);
            if (file) {
              switch (type) {
                case RESOURCE_CONTENT_TYPE.image:
                  addPromptImage(file, randomId, index);
                  insertFileTextToPrompt(editor, isHtmlEmpty, file, RESOURCE_CONTENT_TYPE.image);
                  break;
                case RESOURCE_CONTENT_TYPE.document:
                  addPromptDocument(file, randomId, index);
                  insertFileTextToPrompt(editor, isHtmlEmpty, file, RESOURCE_CONTENT_TYPE.document);
                  break;
                default:
                  break;
              }
            } else {
              switch (type) {
                case PROMPT_MENU_TYPE.document:
                  setDocumentError(
                    id,
                    getResourceDocumentErrorMessage(
                      RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace
                    )
                  );
                  break;
                case PROMPT_MENU_TYPE.image:
                  setImageError(
                    id,
                    getResourceImageErrorMessage(
                      RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace
                    )
                  );
                  break;
                default:
                  break;
              }
            }
            switch (type) {
              case RESOURCE_CONTENT_TYPE.image:
                setIsUploadingImage(false);
                break;
              case RESOURCE_CONTENT_TYPE.document:
                setUploadingDocumentName(null);
                setIsUploadingDocument(false);
                break;
              default:
                break;
            }
            return;
          }
          switch (type) {
            case RESOURCE_CONTENT_TYPE.image:
              setImageError(id, error);
              removeUploadingImage(randomId);
              setIsUploadingImage(false);
              break;
            case RESOURCE_CONTENT_TYPE.document:
              setDocumentError(id, error);
              removeUploadingDocument(randomId);
              setUploadingDocumentName(null);
              setIsUploadingDocument(false);
              break;
            default:
              break;
          }
        },
        pastedFiles: [file],
        onStopLoading: () => {
          switch (type) {
            case RESOURCE_CONTENT_TYPE.image:
              removeUploadingDocument(randomId);
              setIsUploadingImage(false);
              break;
            case RESOURCE_CONTENT_TYPE.document:
              setUploadingDocumentName(null);
              removeUploadingDocument(randomId);
              setIsUploadingDocument(false);
              break;
            default:
              break;
          }
        },
      });
      if (uploadedFile?.status === 'active') {
        switch (type) {
          case RESOURCE_CONTENT_TYPE.image:
            clearImageError();
            handleImageClick(uploadedFile, false, randomId);
            setImageUploadSuccess(uploadedFile);
            break;
          case RESOURCE_CONTENT_TYPE.document:
            clearDocumentError();
            handleDocumentClick(uploadedFile, false, randomId);
            setDocumentUploadSuccess(uploadedFile);
            break;
          default:
            break;
        }
      }
      setIsUploadingImage(false);
    },
    [
      openAddResourcePopup,
      setIsUploadingImage,
      index,
      setUploadingDocumentName,
      setIsUploadingDocument,
      imageResources,
      documentResources,
      addPromptImage,
      editor,
      isHtmlEmpty,
      addPromptDocument,
      setDocumentError,
      setImageError,
      removeUploadingImage,
      removeUploadingDocument,
      clearImageError,
      handleImageClick,
      setImageUploadSuccess,
      clearDocumentError,
      handleDocumentClick,
      setDocumentUploadSuccess,
    ]
  );

  useEffect(() => {
    const pasteListener = async (e) => {
      if (disablePaste) {
        return;
      }

      if (!e?.clipboardData?.files?.length) {
        return;
      }

      const uploadedFile = e.clipboardData.files[0];
      const type = uploadedFile.type.startsWith(RESOURCE_CONTENT_TYPE.image)
        ? RESOURCE_CONTENT_TYPE.image
        : RESOURCE_CONTENT_TYPE.document;

      if (shouldOpenConfirmPopup([type], uploadedFile)) {
        onDisplayMessage(`You can use a maximum of ${MAX_RESOURCE_PER_TYPE} ${type}s`);
        return;
      }

      uploadFile(uploadedFile);
    };
    const thisRef = pasteRef?.current;
    if (!thisRef) {
      return;
    }
    thisRef.addEventListener('paste', pasteListener);
    return () => {
      thisRef.removeEventListener('paste', pasteListener);
    };
  }, [uploadFile, shouldOpenConfirmPopup, disablePaste, pasteRef, isMultiPrompt, onDisplayMessage]);

  return (
    <div ref={pasteRef}>
      <div
        onClick={(event) => {
          if (event.currentTarget === event.target || event.currentTarget.contains(event.target)) {
            // process only if the click is on this element or its descendant
            onEditorFocus?.();
          }
        }}
        ref={(el) => {
          if (isFirstField) {
            firstFieldRef.current = el;
            setIsFirstRefVisible(!!el);
          }
        }}
      >
        <FormControl className={cx(styles.root, className)}>
          <RichSlateBrandBriefsContext.Provider value={brandBriefs}>
            <InstructionsTextareaWrapper
              className={styles.input_root}
              setPlusIconClicked={setPlusIconClicked}
              onMenuButtonClick={handleMenuButtonClick}
              plusIconClicked={plusIconClicked}
              setWasOpened={setWasOpened}
              value={value}
              lastPromptAsset={lastPromptAsset}
              lastPromptUrl={lastPromptUrl}
              onChange={onChange}
              error={error}
              editor={editor}
              onCloseMenu={handleCloseMenu}
              classes={editableClasses}
              loadingFreestyleToolContent={loadingFreestyleToolContent}
              isSuggestPromptLoading={isSuggestPromptLoading}
              freestyleToolContentId={freestyleToolContentId}
              onKeyDown={handleKeyDown}
              generationTool={generationTool}
              isSavePopup={isSavePopup}
              onEditorFocus={onEditorFocus}
              setMenuType={setMenuType}
              showOnFocus={showOnFocus}
              shortExpanding={shortExpanding}
              noTags={noTags}
              autoShowMenu={autoShowMenu}
              placeHolder={placeHolder}
              footerShown={!hideActions && !disabled}
              promptImages={promptImages}
              plusButtonRef={plusButtonRef}
              setPlusButtonRef={setPlusButtonRef}
              isFirstField={isFirstField}
              noMinMaxHeight={noMinMaxHeight}
              onMenuOpen={handleOpenMenuAtCursor}
              trackingEvents={trackingEvents}
              isHtmlEmpty={isHtmlEmpty}
              cachedValue={cachedValue}
              onQuickSearchTriggered={handleQuickSearchTriggered}
              disabled={editorDisabled}
            />
          </RichSlateBrandBriefsContext.Provider>

          {!disabled && (
            <Footer
              isSuggestPromptLoading={isSuggestPromptLoading}
              disabled={disabled}
              onSuggestPromptClick={onSuggestPromptClick}
              onSave={onSave}
              promptImages={promptImages}
              isUploadingImage={isUploadingImage}
              promptText={value}
              promptDocuments={promptDocuments}
              isUploadingDocument={isUploadingDocument}
              uploadingDocumentName={uploadingDocumentName}
              editor={editor}
              hideActions={hideActions}
              index={index}
              isPersonalizedPrompt={isPersonalizedPrompt}
              ratingsCount={ratingsCount}
              showRatingsBanner={showRatingsBanner}
              setShowRatingsBanner={setShowRatingsBanner}
              generationTool={generationTool}
              onClearVariationRatings={onClearVariationRatings}
              showPersonalizePrompt={showPersonalizePrompt}
              removePromptDocument={removePromptDocument}
              removePromptImage={removePromptImage}
              isSlim={isSlim}
              uploadingDocuments={uploadingDocuments}
              uploadingImages={uploadingImages}
              onOpenResourceInNewTab={handleOpenResourceInNewTab}
              showImprovePrompt={generationTool !== GENERATION_TOOL.imageWithDescription}
            />
          )}

          <PromptMenu
            menuAnchor={menuAnchor}
            menuVisible={menuVisible}
            menuProps={menuProps}
            ref={menuRef}
            menuType={menuType}
            setMenuType={setMenuType}
            onCloseMenu={handleCloseMenu}
            onKeyDownMenu={handleKeyDownMenu}
            showAllResources={showAllResources}
            setShowAllResources={setShowAllResources}
            onUrlClick={handleUrlClick}
            onImageClick={handleImageClick}
            noTags={noTags}
            urlResources={urlResources}
            imageResources={imageResources}
            trackingEvents={trackingEvents}
            onBankMessageClick={handleBankMessageClick}
            editor={editor}
            brandBriefsMenuItems={brandBriefsMenuItems}
            openAddResourcePopup={openAddResourcePopup}
            addPromptImage={addPromptImage}
            setIsUploadingImage={setIsUploadingImage}
            setImageError={setImageError}
            clearImageError={clearImageError}
            isApplicationModeInfinity={isApplicationModeInfinity}
            isUploadingImage={isUploadingImage}
            hideImages={hideImages}
            generationTool={generationTool}
            setImageUploadSuccess={setImageUploadSuccess}
            addPromptDocument={addPromptDocument}
            documentResources={documentResources}
            onDocumentClick={handleDocumentClick}
            isUploadingDocument={isUploadingDocument}
            setDocumentUploadSuccess={setDocumentUploadSuccess}
            setIsUploadingDocument={setIsUploadingDocument}
            setUploadingDocumentName={setUploadingDocumentName}
            clearDocumentError={clearDocumentError}
            setDocumentError={setDocumentError}
            openConfirmPopup={openConfirmPopup}
            index={index}
            shouldOpenConfirmPopup={shouldOpenConfirmPopup}
            isMultiPrompt={isMultiPrompt}
            removeUploadingDocument={removeUploadingDocument}
            removeUploadingImage={removeUploadingImage}
            onDisplayMessage={onDisplayMessage}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            quickSearchMode={quickSearchMode}
            onOpenResourceInNewTab={handleOpenResourceInNewTab}
            externalChannelPromptMenuProps={externalChannelPromptMenuProps}
            onAction={onAction}
          />
        </FormControl>
      </div>
    </div>
  );
};

PromptInstructions.propTypes = {
  className: PropTypes.string,
  error: PropTypes.bool,
  value: PropTypes.string,
  lastPromptAsset: PropTypes.object,
  lastPromptUrl: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onSuggestPromptClick: PropTypes.func,
  isSuggestPromptLoading: PropTypes.bool,
  onSave: PropTypes.func,
  brandBriefs: PropTypes.arrayOf(PropTypes.object),
  brandBriefsMenuItems: PropTypes.arrayOf(PropTypes.object),
  onBankMessageClick: PropTypes.func.isRequired,
  hideActions: PropTypes.bool,
  editableClasses: PropTypes.object,
  loadingFreestyleToolContent: PropTypes.bool,
  freestyleToolContentId: PropTypes.string,
  generationTool: PropTypes.string,
  urlResources: PropTypes.arrayOf(PropTypes.object),
  imageResources: PropTypes.arrayOf(PropTypes.object),
  addPromptImage: PropTypes.func,
  isSavePopup: PropTypes.bool,
  onEditorFocus: PropTypes.func,
  freestyleToolId: PropTypes.string,
  shortExpanding: PropTypes.bool,
  noTags: PropTypes.bool,
  autoShowMenu: PropTypes.bool,
  showOnFocus: PropTypes.bool,
  disableAutoFocus: PropTypes.bool,
  placeHolder: PropTypes.string,
  isFirstField: PropTypes.bool,
  plusButtonRef: PropTypes.object,
  setPlusButtonRef: PropTypes.func,
  noMinMaxHeight: PropTypes.bool,
  promptImages: PropTypes.shape(),
  openAddResourcePopup: PropTypes.func.isRequired,
  isUploadingImage: PropTypes.bool,
  setIsUploadingImage: PropTypes.func,
  setImageError: PropTypes.func,
  clearImageError: PropTypes.func,
  isApplicationModeInfinity: PropTypes.bool,
  trackingEvents: PropTypes.object,
  hideImages: PropTypes.bool,
  setImageUploadSuccess: PropTypes.func,
  isHtmlEmpty: PropTypes.bool,
  addPromptDocument: PropTypes.func,
  isUploadingDocument: PropTypes.bool,
  setIsUploadingDocument: PropTypes.func,
  setDocumentError: PropTypes.func,
  promptDocuments: PropTypes.shape(),
  documentResources: PropTypes.arrayOf(PropTypes.object),
  setDocumentUploadSuccess: PropTypes.func,
  clearDocumentError: PropTypes.func,
  setUploadingDocumentName: PropTypes.func,
  uploadingDocumentName: PropTypes.string,
  openConfirmPopup: PropTypes.func.isRequired,
  index: PropTypes.number,
  setShouldUpdateFileText: PropTypes.func,
  shouldUpdateFileText: PropTypes.object,
  shouldOpenConfirmPopup: PropTypes.func.isRequired,
  disablePaste: PropTypes.bool,
  isPersonalizedPrompt: PropTypes.bool,
  ratingsCount: PropTypes.shape(),
  showRatingsBanner: PropTypes.bool,
  setShowRatingsBanner: PropTypes.func,
  onClearVariationRatings: PropTypes.func,
  showPersonalizePrompt: PropTypes.bool,
  cachedValue: PropTypes.string,
  removePromptDocument: PropTypes.func,
  removePromptImage: PropTypes.func,
  isMultiPrompt: PropTypes.bool,
  isSlim: PropTypes.bool,
  uploadingDocuments: PropTypes.arrayOf(PropTypes.object),
  removeUploadingDocument: PropTypes.func,
  removeUploadingImage: PropTypes.func,
  uploadingImages: PropTypes.arrayOf(PropTypes.object),
  onDisplayMessage: PropTypes.func.isRequired,
  editorDisabled: PropTypes.bool,
  externalChannelPromptMenuProps: PropTypes.shape({}),
  onAction: PropTypes.func.isRequired,
};

PromptInstructions.defaultProps = {
  className: null,
  error: false,
  value: undefined,
  lastPromptAsset: null,
  lastPromptUrl: null,
  disabled: false,
  onSuggestPromptClick: null,
  isSuggestPromptLoading: false,
  onSave: null,
  brandBriefs: [],
  brandBriefsMenuItems: [],
  hideActions: false,
  editableClasses: null,
  loadingFreestyleToolContent: false,
  freestyleToolContentId: null,
  generationTool: null,
  urlResources: [],
  imageResources: [],
  addPromptImage: null,
  isSavePopup: false,
  onEditorFocus: null,
  freestyleToolId: null,
  shortExpanding: false,
  noTags: false,
  autoShowMenu: false,
  showOnFocus: false,
  disableAutoFocus: false,
  placeHolder: null,
  isFirstField: false,
  plusButtonRef: null,
  setPlusButtonRef: null,
  noMinMaxHeight: false,
  promptImages: null,
  isUploadingImage: false,
  setIsUploadingImage: null,
  setImageError: null,
  clearImageError: null,
  isApplicationModeInfinity: false,
  trackingEvents: null,
  hideImages: false,
  setImageUploadSuccess: null,
  isHtmlEmpty: false,
  addPromptDocument: null,
  isUploadingDocument: false,
  setIsUploadingDocument: null,
  setDocumentError: null,
  promptDocuments: null,
  documentResources: [],
  uploadingDocumentName: null,
  setDocumentUploadSuccess: null,
  clearDocumentError: null,
  setUploadingDocumentName: null,
  index: 0,
  setShouldUpdateFileText: null,
  shouldUpdateFileText: null,
  disablePaste: false,
  isPersonalizedPrompt: false,
  ratingsCount: null,
  showRatingsBanner: false,
  setShowRatingsBanner: null,
  onClearVariationRatings: null,
  showPersonalizePrompt: false,
  cachedValue: undefined,
  removePromptDocument: null,
  removePromptImage: null,
  isMultiPrompt: false,
  isSlim: false,
  removeUploadingDocument: null,
  removeUploadingImage: null,
  uploadingDocuments: [],
  uploadingImages: [],
  editorDisabled: false,
  externalChannelPromptMenuProps: null,
};
